import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { login as loginApi } from '../../api/auth';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let particles: { x: number; y: number; vx: number; vy: number; r: number; red: boolean }[] = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const init = () => {
      resize();
      particles = Array.from({ length: 70 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 1.5 + 0.5,
        red: Math.random() > 0.88,
      }));
    };

    const draw = () => {
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      // Grid
      ctx.strokeStyle = 'rgba(255,255,255,0.015)';
      ctx.lineWidth = 0.5;
      for (let x = 0; x < w; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
      for (let y = 0; y < h; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }

      // Lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 110) {
            ctx.strokeStyle = `rgba(255,255,255,${0.055 * (1 - d / 110)})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Particles
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.red ? '#DC2626' : 'rgba(255,255,255,0.5)';
        ctx.fill();
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
      });

      animId = requestAnimationFrame(draw);
    };

    init();
    draw();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await loginApi(username, password);
      login(data);
      navigate('/dashboard');
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError(err?.response?.data?.error || 'Çok fazla deneme yapıldı. 5 dakika sonra tekrar deneyin.');
      } else {
        setError('Kullanıcı adı veya şifre hatalı');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      background: '#0A1628', position: 'relative', overflow: 'hidden',
    }}>
      {/* Animated canvas background */}
      <canvas ref={canvasRef} style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%', zIndex: 0,
      }} />

      {/* Left panel */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        justifyContent: 'center', padding: '60px',
        position: 'relative', zIndex: 1,
      }}>
        <div style={{ marginBottom: '32px' }}>
          <img src="/klc-logo.png" alt="KLC Kablo"
            style={{ height: '110px', objectFit: 'contain' }} />
        </div>

        <div style={{ maxWidth: '420px' }}>
          <div style={{
            fontSize: '12px', color: '#DC2626',
            fontFamily: 'Barlow Condensed, sans-serif',
            letterSpacing: '3px', fontWeight: '600', marginBottom: '16px',
          }}>QUOTATION MANAGEMENT SYSTEM</div>

          <h1 style={{
            fontFamily: 'Barlow Condensed, sans-serif',
            fontSize: '54px', fontWeight: '800',
            color: 'white', lineHeight: '1.05', marginBottom: '24px',
          }}>
            Welcome<br />Back
          </h1>

          <p style={{
            color: 'rgba(255,255,255,0.4)',
            fontSize: '15px', lineHeight: '1.7', maxWidth: '320px',
          }}>
            Sign in to KLC Cable's quotation management system.
            Create professional quotes quickly and efficiently.
          </p>

          {/* Stats */}
          <div style={{
            display: 'flex', gap: '32px', marginTop: '40px',
            paddingTop: '32px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}>
            {[
              { label: 'Cable Families', value: '8+' },
              { label: 'Products', value: '500+' },
              { label: 'PDF Export', value: 'TR / EN' },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: 'white', fontFamily: 'Barlow Condensed, sans-serif' }}>{value}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', marginTop: '2px' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom decoration */}
        <div style={{
          position: 'absolute', bottom: '40px', left: '60px',
          display: 'flex', gap: '8px',
        }}>
          {['#DC2626', '#1A3A6B', 'rgba(255,255,255,0.2)'].map((c, i) => (
            <div key={i} style={{
              width: i === 0 ? '32px' : '8px',
              height: '3px', background: c, borderRadius: '2px',
            }} />
          ))}
        </div>
      </div>

      {/* Divider */}
      <div style={{
        width: '1px',
        background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.1) 30%, rgba(255,255,255,0.1) 70%, transparent)',
        margin: '40px 0', zIndex: 1, position: 'relative',
      }} />

      {/* Right panel - Form */}
      <div style={{
        width: '480px', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '60px 50px',
        position: 'relative', zIndex: 1,
      }}>
        <div style={{ width: '100%' }}>
          <div style={{
            fontFamily: 'Barlow Condensed, sans-serif',
            fontSize: '12px', letterSpacing: '3px',
            color: 'rgba(255,255,255,0.3)', marginBottom: '32px',
          }}>SIGN IN TO YOUR ACCOUNT</div>

          {error && (
            <div style={{
              background: 'rgba(220,38,38,0.15)',
              border: '1px solid rgba(220,38,38,0.4)',
              borderRadius: '6px', padding: '12px 16px',
              color: '#FCA5A5', fontSize: '14px', marginBottom: '24px',
            }}>{error}</div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block', fontSize: '11px',
                color: 'rgba(255,255,255,0.4)',
                letterSpacing: '2px', marginBottom: '10px',
                fontFamily: 'Barlow Condensed, sans-serif',
              }}>USERNAME</label>
              <input
                type="text" value={username}
                onChange={e => setUsername(e.target.value)}
                required autoFocus
                placeholder="Enter your username"
                style={{
                  width: '100%', padding: '14px 18px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '6px', color: 'white',
                  fontSize: '15px', outline: 'none',
                  fontFamily: 'Barlow, sans-serif',
                  transition: 'border-color 0.2s', boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = '#DC2626'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
              />
            </div>

            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'block', fontSize: '11px',
                color: 'rgba(255,255,255,0.4)',
                letterSpacing: '2px', marginBottom: '10px',
                fontFamily: 'Barlow Condensed, sans-serif',
              }}>PASSWORD</label>
              <input
                type="password" value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                style={{
                  width: '100%', padding: '14px 18px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '6px', color: 'white',
                  fontSize: '15px', outline: 'none',
                  fontFamily: 'Barlow, sans-serif',
                  transition: 'border-color 0.2s', boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = '#DC2626'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
              />
            </div>

            <button
              type="submit" disabled={loading}
              style={{
                width: '100%', padding: '15px',
                background: loading ? '#991B1B' : '#DC2626',
                border: 'none', borderRadius: '6px',
                color: 'white', fontSize: '14px',
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: '700', letterSpacing: '3px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { if (!loading) (e.target as HTMLButtonElement).style.background = '#991B1B'; }}
              onMouseLeave={e => { if (!loading) (e.target as HTMLButtonElement).style.background = '#DC2626'; }}
            >
              {loading ? 'SIGNING IN...' : 'SIGN IN →'}
            </button>
          </form>

          <div style={{
            marginTop: '40px', paddingTop: '24px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            fontSize: '12px', color: 'rgba(255,255,255,0.2)',
            textAlign: 'center',
          }}>
            © 2026 KLC Kablo Alüminyum San. ve Tic. A.Ş.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
